/**
 * `lib/perf` — the frame-time probe. `docs/design-direction.md` §6.1, `spec.md` §17.2.
 *
 * The grain layer (§6.1), the halftone fields (§6.2) and the misregistration ghost
 * (§6.3) are cheap on a desktop GPU and genuinely expensive to composite on the
 * low-end Android hardware that runs a large share of Telegram's install base inside
 * a WebView. Rather than guess a device allowlist, the app measures: it samples the
 * first ~40 animation frames after boot (§6.1's "first ~500ms of the first scroll",
 * approximated here as the first 40 rAF ticks of the boot itself, which is available
 * before any scroll has happened) and takes the **median** frame time — median, not
 * mean, so one long GC pause cannot itself trip the switch, and no run of fast frames
 * can hide a device that is genuinely struggling.
 *
 * Two independent triggers set `html[data-grain="off"]` — "low ink" mode — and either
 * one is sufient on its own, exactly as §6.1's reference snippet ORs them:
 *
 *  1. **Measured**: the median sampled frame exceeds {@link GRAIN_MEDIAN_THRESHOLD_MS}
 *     (22ms — comfortably worse than one 60Hz frame, so ordinary jitter never trips
 *     it).
 *  2. **Declared**: `navigator.hardwareConcurrency <= 4` — a core count low enough
 *     that the measurement is almost redundant, but cheap to check and worth checking
 *     first on a device where even 40 rAF ticks would be a slow, janky half-second.
 *
 * All 40 samples are still collected before checking either condition (rather than
 * short-circuiting on the core count), because that is what §6.1's own snippet does
 * and because the measured branch is the one the design direction actually cites as
 * "measured not guessed" — skipping it on a low core count would make a low-power but
 * otherwise fast device (many 4-core traditional desktops exist) fall back to a
 * measurement that never ran.
 *
 * ## The manual override
 *
 * "Low ink" is also a first-class user choice from the About sheet (§6.1, §12's
 * checklist), and it has to win over the measurement every time — a user who has
 * deliberately turned the atmosphere off must not have it silently turned back on by
 * a probe on their next launch. The override lives in `store/persist`'s own
 * `bl_prefs_v1` blob, under the `lowInk` field that slice already reserves for
 * exactly this (`spec.md` §13.7's "last tab, low-ink toggle"), rather than a second
 * key of its own — two keys for one flag is two places to get out of sync. Reading
 * and writing it here does not create a dependency cycle: `store/persist` never
 * imports `lib/perf`.
 *
 * `localStorage` is a cache a webview is allowed to throw from (§13.7), so every
 * access here is wrapped exactly the way `store/persist` wraps its own.
 */

import type { LocalStorageLike, UiPrefs } from '../store/persist';
import { DEFAULT_PREFS, PREFS_KEY, parsePrefs } from '../store/persist';

/** §6.1: "40 rAF samples". */
export const GRAIN_SAMPLE_COUNT = 40;
/** §6.1: "median frame > 22ms". */
export const GRAIN_MEDIAN_THRESHOLD_MS = 22;
/**
 * §6.1 writes `hardwareConcurrency <= 4`. **That number cannot be used on a phone.**
 *
 * WebKit does not report a core count at all: it returns a fixed anti-fingerprinting
 * value of exactly `4` on every iPhone from the 11 to the 17
 * (mdn/browser-compat-data#30063). Telegram's iOS client is a `WKWebView`, so it is
 * WebKit, so `4 <= 4` was true on **every iPhone ever to open this app** — including
 * the fastest phone Apple sells — and the halftone, the grain and the ghost were
 * stripped from all of them while a desktop browser (8–16 cores) kept them. That is
 * the exact desktop-vs-phone split the operator photographed on 2026-09-05.
 *
 * `2` is the value that still means what §6.1 meant: a device so weak that the
 * measurement below is redundant. Nothing in Telegram's install base reports 2 except
 * genuinely ancient Android hardware, and that hardware also fails the measured
 * branch, which is the branch §6.1 actually calls "measured not guessed".
 */
export const GRAIN_WEAK_CONCURRENCY = 2;
/**
 * How long to wait before the first sampled frame.
 *
 * §6.1 asks for "the first ~500ms of the **first scroll**". The probe used to start
 * fourteen lines after `createRoot().render()` in `main.tsx`, so the 40 frames it
 * measured were React's first mount, the webfont swap and the `POST /api/auth/telegram`
 * round trip — the most expensive frames the app will ever produce, and a median over
 * 22ms there is unremarkable on hardware that then runs at 60fps for the rest of the
 * session. Starting late measures the steady state instead, which is what the switch
 * is supposed to be protecting.
 */
export const GRAIN_PROBE_DELAY_MS = 2500;
/** `?ink=on` / `?ink=off` — the escape hatch, because there is no About sheet yet. */
export const GRAIN_QUERY_PARAM = 'ink';
/** The attribute `texture.css` reads to strip the grain, halftone and ghost layers. */
export const GRAIN_ATTRIBUTE = 'data-grain';

export type GrainState = 'on' | 'off';

export interface ProbeGrainOptions {
  /** Where {@link GRAIN_ATTRIBUTE} is written. Defaults to `document.documentElement`. */
  readonly root?: HTMLElement;
  /** Injectable for tests; defaults to the real `requestAnimationFrame`. */
  readonly requestFrame?: (callback: (time: number) => void) => void;
  /** Injectable for tests; defaults to `performance.now`. */
  readonly now?: () => number;
  /** Overrides `navigator.hardwareConcurrency` for the test that stubs it. */
  readonly hardwareConcurrency?: number;
  readonly sampleCount?: number;
  readonly thresholdMs?: number;
  readonly weakConcurrency?: number;
  /** Delay before the first sample. `0` in tests, so the fake clock stays honest. */
  readonly delayMs?: number;
  /** Injectable for tests; defaults to the real `setTimeout`. */
  readonly wait?: (ms: number) => Promise<void>;
  /** The URL to read `?ink=` from. `null` disables the query override entirely. */
  readonly search?: string | null;
  /**
   * Overrides where the manual toggle is read from. `null` disables the override
   * entirely (the probe always measures) — the shape a test wants when it is
   * exercising the measurement branch in isolation.
   */
  readonly storage?: LocalStorageLike | null;
}

/** §13.7: reading `globalThis.localStorage` is itself the operation that can throw. */
function resolveStorage(explicit: LocalStorageLike | null | undefined): LocalStorageLike | null {
  if (explicit !== undefined) return explicit;
  try {
    const storage: LocalStorageLike | undefined = globalThis.localStorage;
    return storage ?? null;
  } catch {
    return null;
  }
}

function readPrefs(storage: LocalStorageLike): UiPrefs {
  try {
    return parsePrefs(storage.getItem(PREFS_KEY));
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

/**
 * The persisted manual choice, or `null` when the user has never set one — in which
 * case the caller measures instead of assuming.
 */
export function readGrainOverride(storage?: LocalStorageLike | null): GrainState | null {
  const store = resolveStorage(storage);
  if (store === null) return null;
  const prefs = readPrefs(store);
  if (prefs.lowInk === true) return 'off';
  if (prefs.lowInk === false) return 'on';
  return null;
}

/**
 * Persists the manual choice into the shared `bl_prefs_v1` blob. `null` clears it, so
 * the next boot measures again rather than being pinned to a state the user never
 * chose to make permanent.
 *
 * A write failure (quota, a wiped origin, a privacy mode that throws) is swallowed:
 * §13.7 treats `localStorage` as a cache, never a source of truth, and the caller has
 * already applied the state for this session via {@link applyGrainState}.
 */
export function writeGrainOverride(
  value: GrainState | null,
  storage?: LocalStorageLike | null,
): void {
  const store = resolveStorage(storage);
  if (store === null) return;
  const current = readPrefs(store);
  const next: UiPrefs = { ...current, lowInk: value === null ? null : value === 'off' };
  try {
    store.setItem(PREFS_KEY, JSON.stringify(next));
  } catch {
    /* A cache that cannot be written is not a reason to fail the toggle (§13.7). */
  }
}

/** Writes (or leaves) {@link GRAIN_ATTRIBUTE}. Exported so the About sheet's toggle
 * can apply a choice immediately, without waiting for a re-render. */
export function applyGrainState(
  state: GrainState,
  root: HTMLElement = document.documentElement,
): void {
  root.setAttribute(GRAIN_ATTRIBUTE, state);
}

function median(values: readonly number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[sorted.length >> 1] ?? 0;
}

function defaultRequestFrame(callback: (time: number) => void): void {
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(callback);
    return;
  }
  // A headless or very old environment with no rAF at all: keep the probe alive on a
  // plain timer rather than hanging forever.
  setTimeout(() => {
    callback(Date.now());
  }, 16);
}

function defaultWait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * `?ink=on` / `?ink=off`, or `null` when the URL says nothing.
 *
 * §6.1 requires "a manual toggle in the About sheet"; there is no About sheet and no
 * `/about` route, so `writeGrainOverride` has never had a production caller and a
 * phone that tripped the probe had no way back. Until that sheet exists, the query
 * string is the toggle — it survives into `bl_prefs_v1`, so it only has to be typed
 * once.
 */
export function readGrainQuery(search?: string | null): GrainState | null {
  if (search === null) return null;
  const raw =
    search ?? (typeof location === 'undefined' ? '' : `${location.search}${location.hash}`);
  if (raw === '') return null;
  const match = new RegExp(`[?&#]${GRAIN_QUERY_PARAM}=(on|off)\\b`, 'i').exec(raw);
  if (match === null) return null;
  return match[1]?.toLowerCase() === 'off' ? 'off' : 'on';
}

function defaultNow(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}

function hasClampedConcurrency(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return ua.includes('Macintosh') && navigator.maxTouchPoints > 1;
}

function resolveConcurrency(override: number | undefined): number {
  if (typeof override === 'number' && Number.isFinite(override)) return override;
  if (typeof navigator === 'undefined') return 8;
  if (hasClampedConcurrency()) return 8;
  const value = navigator.hardwareConcurrency;
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 8;
}

/** Collects `count` inter-frame deltas via `requestFrame`, resolving with all of them. */
function sampleFrameTimes(
  count: number,
  requestFrame: (callback: (time: number) => void) => void,
  now: () => number,
): Promise<number[]> {
  return new Promise((resolve) => {
    const deltas: number[] = [];
    let last = now();
    let collected = 0;
    const step = (time: number): void => {
      const stamp = Number.isFinite(time) ? time : now();
      deltas.push(stamp - last);
      last = stamp;
      collected += 1;
      if (collected < count) requestFrame(step);
      else resolve(deltas);
    };
    requestFrame(step);
  });
}

/**
 * §6.1's kill switch. Resolves with the state it applied — `'off'` for "low ink".
 *
 * The manual override always wins and, when present, this never touches
 * `requestFrame` at all: a user's explicit choice is not something a measurement
 * gets to re-litigate on the next boot.
 */
export async function probeGrain(options: ProbeGrainOptions = {}): Promise<GrainState> {
  const root = options.root ?? (typeof document === 'undefined' ? null : document.documentElement);

  /* `?ink=on` / `?ink=off` wins over everything and is remembered, so the operator has
     a way to force the atmosphere from a phone with no dev tools and no About sheet.
     It is read before the persisted override on purpose: typing the URL is the newer,
     more deliberate act of the two. */
  const fromQuery = readGrainQuery(options.search);
  if (fromQuery !== null) {
    writeGrainOverride(fromQuery, options.storage);
    if (root !== null) applyGrainState(fromQuery, root);
    return fromQuery;
  }

  const override = readGrainOverride(options.storage);
  if (override !== null) {
    if (root !== null) applyGrainState(override, root);
    return override;
  }

  const requestFrame = options.requestFrame ?? defaultRequestFrame;
  const now = options.now ?? defaultNow;
  const sampleCount = Math.max(1, Math.trunc(options.sampleCount ?? GRAIN_SAMPLE_COUNT));
  const thresholdMs = options.thresholdMs ?? GRAIN_MEDIAN_THRESHOLD_MS;
  const weakConcurrency = options.weakConcurrency ?? GRAIN_WEAK_CONCURRENCY;
  const concurrency = resolveConcurrency(options.hardwareConcurrency);
  const delayMs = Math.max(0, options.delayMs ?? GRAIN_PROBE_DELAY_MS);
  const wait = options.wait ?? defaultWait;

  /* Measure the steady state, not the mount storm — see GRAIN_PROBE_DELAY_MS. */
  if (delayMs > 0) await wait(delayMs);

  const deltas = await sampleFrameTimes(sampleCount, requestFrame, now);
  const frameMedian = median(deltas);
  const state: GrainState =
    frameMedian > thresholdMs || concurrency <= weakConcurrency ? 'off' : 'on';

  if (root !== null) applyGrainState(state, root);
  return state;
}
